/**
 * Derivações puras da aba Painel do dia: em que matilha cada pet entra e como
 * a ocupação da creche é lida. Sem React e sem Supabase, para ser testável.
 */
import type { TableTone } from "@akuna/ui";
import type { DaycareCapacity } from "@/hooks/useDaycareCapacity";
import type { StayPack } from "@/hooks/useStayPacks";
import { isActivityVisibleForPet } from "../activities/activitiesModel";
import { isStayPackActiveOnDate, type StayNature, type StayStatus } from "../schedule/scheduleModel";

/** Os três baldes por que `daycare_capacity` limita, mais o pet sem porte. */
export type PetSize = "small" | "medium" | "large" | "unknown";

/**
 * O porte vem de `pets.size_category_id`, relacionado à tabela global
 * `pet_size_categories`. O `name` de lá é editável, então o balde vem do
 * `display_order`, que é a ordenação canônica.
 */
export function petSizeFromDisplayOrder(displayOrder: number | null | undefined): PetSize {
  if (displayOrder == null) return "unknown";
  if (displayOrder <= 1) return "small";
  if (displayOrder === 2) return "medium";
  return "large";
}

export interface PanelPet {
  reservationId: string;
  /** `stay_reservation_days.id` — o que check-in/check-out grava agora. */
  stayDayId: string;
  petId: string;
  /** Matilha operacional escolhida para a reserva do dia, se houver. */
  stayPackId?: string | null;
  /** Todas as matilhas operacionais escolhidas para a reserva do dia. */
  stayPackIds?: string[];
  petName: string;
  ownerId: string | null;
  ownerName: string;
  nature: StayNature;
  status: StayStatus;
  size: PetSize;
  /** `stay_reservations.period` — "full" | "morning" | "afternoon" | null. */
  period: string | null;
  /** `morning` | `evening` quando a clínica usa dois turnos. */
  arrivalShift?: "morning" | "evening" | null;
  /** `stay_reservations.check_in_time`, normalizado para HH:MM. */
  checkInTime: string | null;
  /** `stay_reservations.check_out_time`, normalizado para HH:MM. */
  checkOutTime: string | null;
  /** Telefone do responsável, para o orçamento no WhatsApp. */
  ownerPhone: string | null;
  isOvernight: boolean;
  inAdaptation: boolean;
  bedLabel: string | null;
  /** "17→21/06" para estadia de vários dias, `null` para diária única. */
  stayRange: string | null;
  /** Data de saída da reserva (`check_out_date ?? stay_date`). */
  lastStayDate: string;
  /** Textos dos alertas do perfil, já achatados para leitura no card. */
  alerts: string[];
  /** Alertas operacionais ativos + alergias registradas no perfil. */
  alertCount?: number;
  /** Nomes dos pets com quem este não se dá bem. */
  incompatibleWith: string[];
  /** `true` quando o mês/dia de `pets.birth_date` bate com o dia do painel. */
  isBirthday: boolean;
}

export interface OwnerPhoneRow {
  phone_number: string | null;
  is_primary: boolean | null;
}

/**
 * `owners_phones` guarda vários números por responsável. O primário é o que a
 * clínica usa para falar; sem primário marcado, vale o primeiro cadastrado.
 */
export function primaryOwnerPhone(phones: OwnerPhoneRow[] | null | undefined): string | null {
  const rows = (phones ?? []).filter((row) => Boolean(row.phone_number));
  const primary = rows.find((row) => row.is_primary);
  return (primary ?? rows[0])?.phone_number ?? null;
}

/**
 * Colunas `time` do Postgres chegam como "HH:MM:SS"; `<input type="time">` só
 * aceita "HH:MM".
 */
export function normalizeStayTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})/.exec(value);
  return match ? `${match[1]}:${match[2]}` : null;
}

export const STATUS_TONE: Record<PanelPet["status"], TableTone> = {
  reserved: "neutral",
  checked_in: "blue",
  checked_out: "green",
  cancelled: "red",
  no_show: "neutral",
};

export const SIZE_TONE: Record<PanelPet["size"], TableTone> = {
  small: "blue",
  medium: "amber",
  large: "purple",
  unknown: "neutral",
};

export function sortPanelPets(pets: PanelPet[]): PanelPet[] {
  return pets.slice().sort((left, right) => Number(left.status === "checked_out") - Number(right.status === "checked_out"));
}

/**
 * O checkout é a entrega do pet ao responsável: só faz sentido com o pet
 * presente e no último dia da reserva. Num hotel de vários dias, os dias do
 * meio não têm saída a registrar.
 */
export function canCheckOut(pet: PanelPet, isoDate: string): boolean {
  if (pet.status !== "checked_in") return false;

  // O hotel ocupa noites: a data de checkout é exclusiva e não gera uma
  // `stay_reservation_day`. Portanto, o checkout é feito na última noite.
  if (pet.nature === "hotel") {
    const [year, month, day] = pet.lastStayDate.split("-").map(Number);
    const lastNight = new Date(year, month - 1, day - 1, 12);
    const lastNightIso = `${lastNight.getFullYear()}-${String(lastNight.getMonth() + 1).padStart(2, "0")}-${String(lastNight.getDate()).padStart(2, "0")}`;
    return lastNightIso === isoDate;
  }

  return pet.lastStayDate === isoDate;
}

/** Grupo sintético para quem tem reserva hoje mas nenhuma matilha ativa. */
export const UNPACKED_GROUP_ID = "__unpacked__";

export interface PanelPackGroup {
  id: string;
  /** `null` no grupo sintético — quem renderiza escolhe o rótulo traduzido. */
  name: string | null;
  color: string | null;
  pets: PanelPet[];
  hasAggressivePet: boolean;
}

export interface PanelActivity {
  targetType: "pet" | "pack" | "all_packs" | "general";
  targetPetId: string | null;
  targetPackId: string | null;
  scheduledTime: string | null;
  time: string;
  label: string;
  done: boolean;
}

export interface PanelPetCardDetails {
  packs: Array<{ name: string; color: string | null }>;
  nextActivity: { time: string; label: string } | null;
}

const AGGRESSION_PATTERN = /agressiv|aggressive/i;

export function hasAggressiveAlert(pet: PanelPet) {
  return pet.alerts.some((alert) => AGGRESSION_PATTERN.test(alert));
}

export function operationalPackIds(pet: Pick<PanelPet, "stayPackId" | "stayPackIds">): string[] {
  return [...new Set([...(pet.stayPackIds ?? []), ...(pet.stayPackId ? [pet.stayPackId] : [])])];
}

export function panelAlertCountByPetId(
  alerts: Array<{ petId: string; isActive: boolean }>,
  allergies: Array<{ petId: string }>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const alert of alerts) {
    if (alert.isActive) counts.set(alert.petId, (counts.get(alert.petId) ?? 0) + 1);
  }
  for (const allergy of allergies) {
    counts.set(allergy.petId, (counts.get(allergy.petId) ?? 0) + 1);
  }
  return counts;
}

/**
 * Um pet pode estar em várias matilhas. Por isso ele é renderizado em cada
 * grupo ativo ao qual pertence; o grupo "sem matilha" só recebe quem não está
 * em nenhuma matilha ativa hoje.
 */
export function groupPetsByPack(pets: PanelPet[], packs: StayPack[], isoDate: string): PanelPackGroup[] {
  const activePacks = packs
    .filter((pack) => isStayPackActiveOnDate(pack, isoDate))
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name));

  const groups: PanelPackGroup[] = activePacks.map((pack) => {
    const memberIds = new Set(pack.members.map((member) => member.petId));
    const groupPets = sortPanelPets(pets.filter((pet) => memberIds.has(pet.petId) || operationalPackIds(pet).includes(pack.id)));
    return {
      id: pack.id,
      name: pack.name,
      color: pack.color,
      pets: groupPets,
      hasAggressivePet: groupPets.some(hasAggressiveAlert),
    };
  });

  const packedPetIds = new Set(activePacks.flatMap((pack) => [
    ...pack.members.map((member) => member.petId),
    ...pets.filter((pet) => operationalPackIds(pet).includes(pack.id)).map((pet) => pet.petId),
  ]));
  const unpacked = sortPanelPets(pets.filter((pet) => !packedPetIds.has(pet.petId)));
  if (unpacked.length > 0) {
    groups.push({
      id: UNPACKED_GROUP_ID,
      name: null,
      color: null,
      pets: unpacked,
      hasAggressivePet: unpacked.some(hasAggressiveAlert),
    });
  }

  return groups;
}

/**
 * Dados compactos usados no card da grade. A lista segue exatamente as
 * matilhas ativas da visão Matilhas.
 */
export function panelPetCardDetailsByPetId(
  pets: PanelPet[],
  packs: StayPack[],
  activities: PanelActivity[],
  isoDate: string,
): Map<string, PanelPetCardDetails> {
  const activePacks = packs
    .filter((pack) => isStayPackActiveOnDate(pack, isoDate))
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name));
  const packsByPetId = new Map<string, StayPack[]>();
  for (const pack of activePacks) {
    const petIds = new Set([
      ...pack.members.map((member) => member.petId),
      ...pets.filter((pet) => operationalPackIds(pet).includes(pack.id)).map((pet) => pet.petId),
    ]);
    for (const petId of petIds) {
      const petPacks = packsByPetId.get(petId) ?? [];
      petPacks.push(pack);
      packsByPetId.set(petId, petPacks);
    }
  }

  return new Map(pets.map((pet) => {
    const petPacks = packsByPetId.get(pet.petId) ?? [];
    const packIds = new Set(petPacks.map((pack) => pack.id));
    const nextActivity = activities
      .filter((activity) => !activity.done
        && isActivityVisibleForPet(activity.targetType, activity.targetPetId, pet.petId)
        && (activity.targetType !== "pack" || (activity.targetPackId !== null && packIds.has(activity.targetPackId))))
      .sort((left, right) => (left.scheduledTime ?? "~").localeCompare(right.scheduledTime ?? "~"))[0] ?? null;

    return [pet.petId, {
      packs: petPacks.map((pack) => ({ name: pack.name, color: pack.color })),
      nextActivity: nextActivity ? { time: nextActivity.time, label: nextActivity.label } : null,
    }];
  }));
}

export interface SizeOccupancy {
  size: Exclude<PetSize, "unknown">;
  used: number;
  /** `null` quando a clínica não limita por porte. */
  capacity: number | null;
}

export interface DaycareOccupancy {
  used: number;
  /** `null` quando a clínica nunca configurou capacidade. */
  total: number | null;
  bySize: SizeOccupancy[];
}

const DAYCARE_SIZES: Array<Exclude<PetSize, "unknown">> = ["small", "medium", "large"];

export function daycareOccupancy(pets: PanelPet[], capacity: DaycareCapacity): DaycareOccupancy {
  const daycarePets = pets.filter((pet) => pet.nature !== "hotel");
  const configured = {
    small: capacity.capacitySmall,
    medium: capacity.capacityMedium,
    large: capacity.capacityLarge,
  };

  return {
    used: daycarePets.length,
    total: capacity.limitBySize
      ? (capacity.capacitySmall ?? 0) + (capacity.capacityMedium ?? 0) + (capacity.capacityLarge ?? 0)
      : capacity.totalCapacity,
    bySize: DAYCARE_SIZES.map((size) => ({
      size,
      used: daycarePets.filter((pet) => pet.size === size).length,
      capacity: capacity.limitBySize ? configured[size] : null,
    })),
  };
}
